import { Injectable, Signal, computed, signal } from '@angular/core';
import { SurveyAnswerDto, SurveyProgressDto, SurveyQuestion } from '@shared/models/survey.model';

@Injectable({
  providedIn: 'root'
})
export class SurveySessionService {
  private readonly questionsState = signal<SurveyQuestion[]>([]);
  private readonly indexState = signal(0);
  private readonly answers = signal<SurveyAnswerDto[]>([]);
  private readonly surveyIdState = signal<string | null>(null);

  readonly currentIndex = this.indexState.asReadonly();
  readonly totalQuestions = computed(() => this.questionsState().length);

  setSurvey(id: string, questions: SurveyQuestion[]): void {
    this.surveyIdState.set(id);
    this.questionsState.set(questions);
    this.indexState.set(0);
    this.answers.set([]);
  }

  getCurrentQuestion(): SurveyQuestion | null {
    const list = this.questionsState();
    const index = this.indexState();
    return list[index] ?? null;
  }

  submitAnswer(answer: SurveyAnswerDto): void {
    this.answers.update((items) => {
      const filtered = items.filter((item) => item.questionId !== answer.questionId);
      return [...filtered, answer];
    });

    if (this.indexState() < this.totalQuestions() - 1) {
      this.indexState.update((value) => value + 1);
    }
  }

  goPrevious(): void {
    if (this.indexState() > 0) {
      this.indexState.update((value) => value - 1);
    }
  }

  getProgress(): SurveyProgressDto | null {
    const surveyId = this.surveyIdState();
    if (!surveyId) {
      return null;
    }

    return {
      surveyId,
      currentIndex: this.indexState(),
      totalQuestions: this.totalQuestions(),
      completed: this.indexState() >= this.totalQuestions() - 1
    };
  }

  getAnswers(): SurveyAnswerDto[] {
    return this.answers();
  }

  /**
   * Полный сброс состояния (на logout). Без этого новый пациент видит
   * вопросы/ответы предыдущей сессии.
   */
  clear(): void {
    this.questionsState.set([]);
    this.indexState.set(0);
    this.answers.set([]);
    this.surveyIdState.set(null);
  }
}
